import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import {
  Router,
} from 'react-router-dom';
import { triggerHapticFeedback } from '../../../hapticFeedback';
import { useGetCurrentUser } from '../../../hooks/useGetCurrentUser.ts';
import CordovaPushNotificationService from '../../../services/cordova/CordovaPushNotificationService';
import { AppFeedbackModal } from '../AppFeedbackModal.tsx';
import DevicesMap from './DevicesMap.jsx';
import { ModalRouter } from './ModalRouter.tsx';
import NavDrawer from '../NavDrawer.jsx';
import Loading from '../Loading.jsx';
import Login from '../Login.jsx';
import { useModalState } from '../../../hooks/modal/ModalStateProvider.tsx';

/**
 * This component renders the main dashboard view including router, nav drawer, and map.
 * 
 * A loader is shown while the user is being fetched. If the user is not logged in, the login
 * component is shown. If the user is logged in, the main dashboard is shown.
 * 
 * @returns 
 */
const AccountDashboardRouter: React.FC = () => {
  /* Normally useGetCurrentUser() triggers a page reload on auth error (because it uses customFetchV2).
   * However, here we dont want to reload the page and instead we would just render the login component
   * below. */
  const { data: user, isLoading, invalidateUser } = useGetCurrentUser({ refetchOnInterval: true, skipReloadOnAuthError: true });
  const [navOpen, setNavOpen] = useState(false);
  const { open: isModalOpen } = useModalState();
  const [hideFeedbackRequest, setHideFeedbackRequest] = useState(false);

  /* Tapping nav drawer should give some hapic feedback */
  const onNavOpen = () => {
    triggerHapticFeedback();
    setNavOpen(true);
  };

  useEffect(() => {
    // Register for push notifications if we're on mobile
    if (!user?.email) {
      return;
    }

    Sentry.setUser({ email: user.email });
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `User has active session (${user.email})`,
      level: Sentry.Severity.Info,
    });

    if (process.env.PLATFORM === 'mobile') {
      new CordovaPushNotificationService().registerForPush();
    }
  }, [user?.email]);

  if (isLoading) {
    return (
      <section className="hero is-light is-fullheight">
        <div className="hero-body">
          <div className="container">
            <Loading />
          </div>
        </div>
      </section>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <Router basename="/dashboard" history={window.shieldHistory}>
      <div className="dashboard">
        <NavDrawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          user={user}
        />

        <div className="dashboard-content">
          <div className="hamburger">
            <IconButton id="hamburgerBtn" onClick={onNavOpen} style={{ padding: '10px' }}>
              <MenuIcon style={{ color: '#4a4a4a', fontSize: '33px' }} />
            </IconButton>
          </div>
          <DevicesMap user={user} deviceIds={user.activeDeviceIds} />

          <ModalRouter />

          {/* Show the app feedback modal if the user has an open request and no other modal is open.
            * There's a small issue with this implementation - if the user is re-queried in the background
            * and returns back a null openAppFeedbackRequest, then the modal is immediately closed. If that 
            * happens in the 2 second period after feedback submission then the user doesnt get a chance to
            * read the full success message. But it's not noticable. 
            * 
            */}
          <AppFeedbackModal 
            appFeedbackRequest={isModalOpen || hideFeedbackRequest ? undefined : user?.openAppFeedbackRequest}
            onFeedbackSubmitted={(_rating, _notes) => {
              setTimeout(() => { 
                // Just safer to immediately hide, incase any delay or hiccups refreshing the user
                setHideFeedbackRequest(true);
                invalidateUser();
              }, 3000);
            }}
            onCancel={() => setHideFeedbackRequest(true)}
          />
        </div>
      </div>
    </Router>
  )
};

export default AccountDashboardRouter;
